import { useQuery } from "@tanstack/react-query";
import axios from "axios";

const toNumber = (value, fallback = 0) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
};

const buildDisabledSummary = (customer, message) => ({
    customer: customer || null,
    hasCustomer: Boolean(customer),
    hasProgram: false,
    enabled: false,
    canRedeem: false,
    disabledReason: message,
    loyaltyProgram: null,
    loyaltyProgramName: null,
    loyaltyPoints: 0,
    totalSpent: 0,
    conversionFactor: 0,
    minimumRedemption: 0,
    maximumRedemption: 0,
    maxDailyRedemptions: 0,
    timesRedeemedToday: 0,
    remainingDailyRedemptions: null,
    tierName: null,
    collectionFactor: 0,
    redemptionValue: 0,
    raw: null,
});

const normalizeLoyaltyResponse = (raw, customer) => {
    if (!customer) {
        return buildDisabledSummary(null, "Select a customer to view loyalty details");
    }

    if (!raw || !raw.loyalty_program) {
        return buildDisabledSummary(
            customer,
            "Customer is not enrolled in a loyalty program"
        );
    }

    const loyaltyPoints = Math.max(0, Math.floor(toNumber(raw.loyalty_points)));
    const conversionFactor = Math.max(0, toNumber(raw.conversion_factor));
    const minimumRedemption = Math.max(0, Math.floor(toNumber(raw.minimum_redemption)));
    const maximumRedemption = Math.max(0, Math.floor(toNumber(raw.maximum_redemption)));
    const maxDailyRedemptions = Math.max(
        0,
        Math.floor(toNumber(raw.max_number_of_daily_redemptions))
    );
    const timesRedeemedToday = Math.max(
        0,
        Math.floor(toNumber(raw.times_redeemed_today))
    );
    const remainingDailyRedemptions =
        maxDailyRedemptions > 0
            ? Math.max(0, maxDailyRedemptions - timesRedeemedToday)
            : null;

    let disabledReason = "";
    if (!loyaltyPoints) {
        disabledReason = "Customer does not have any loyalty points to redeem";
    } else if (!conversionFactor) {
        disabledReason = "Loyalty conversion factor is not configured";
    } else if (remainingDailyRedemptions === 0) {
        disabledReason = "Customer has reached the daily loyalty redemption limit";
    }

    return {
        customer,
        hasCustomer: true,
        hasProgram: true,
        enabled: !disabledReason,
        canRedeem: !disabledReason,
        disabledReason,
        loyaltyProgram: raw.loyalty_program || null,
        loyaltyProgramName:
            raw.loyalty_program_name || raw.program_name || raw.loyalty_program || null,
        loyaltyPoints,
        totalSpent: Math.max(0, toNumber(raw.total_spent)),
        conversionFactor,
        minimumRedemption,
        maximumRedemption,
        maxDailyRedemptions,
        timesRedeemedToday,
        remainingDailyRedemptions,
        tierName: raw.tier_name || null,
        collectionFactor: Math.max(0, toNumber(raw.collection_factor)),
        redemptionValue: Number((loyaltyPoints * conversionFactor).toFixed(2)),
        expenseAccount: raw.expense_account || null,
        costCenter: raw.cost_center || null,
        raw,
    };
};

const fetchCustomerLoyaltyProgram = async ({ customer, company }) => {
    const params = { customer };
    if (company) {
        params.company = company;
    }

    const { data } = await axios.get(
        "method/custom_api.api.get_customer_loyalty_program",
        { params }
    );

    return data?.message ?? data;
};

export const useCustomerLoyaltyProgram = (customer, company, options = {}) => {
    const enabled = (options.enabled ?? true) && Boolean(customer);
    const query = useQuery(
        ["customerLoyaltyProgram", customer, company],
        () => fetchCustomerLoyaltyProgram({ customer, company }),
        {
            enabled,
            refetchOnWindowFocus: false,
            staleTime: 30 * 1000,
            keepPreviousData: true,
        }
    );

    return {
        ...query,
        data: normalizeLoyaltyResponse(query.data, customer),
    };
};

export const calculateLoyaltyRedemptionAmount = (
    points,
    conversionFactor,
    maxAmount = Infinity
) => {
    const amount = Math.max(0, toNumber(points) * toNumber(conversionFactor));
    const cappedAmount = Math.min(amount, Math.max(0, toNumber(maxAmount, Infinity)));
    return Number(cappedAmount.toFixed(2));
};
